import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2, CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [status, setStatus] = useState("loading"); // loading | valid | invalid | expired | done
  const [tokenInfo, setTokenInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const validate = useCallback(async () => {
    if (!token) { setStatus("invalid"); return; }
    try {
      const { data } = await api.get(`/auth/validate-token/${token}`);
      if (data.valid) {
        setTokenInfo(data);
        setStatus("valid");
      } else {
        setStatus(data.reason === "expired" ? "expired" : "invalid");
      }
    } catch {
      setStatus("invalid");
    }
  }, [token]);

  useEffect(() => { validate(); }, [validate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/set-password", { token, password });
      if (data.token) {
        // Store access token in memory only (httpOnly cookie handles persistence)
        const { setAccessToken } = await import("@/lib/api");
        setAccessToken(data.token);
      }
      setStatus("done");
      toast.success("Password set successfully!");
      setTimeout(() => nav("/m"), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to set password. Token may have expired.");
    }
    setSaving(false);
  };

  if (status === "loading") {
    return (
      <PublicLayout>
        <div className="flex-1 flex items-center justify-center py-16 px-4">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PublicLayout>
    );
  }

  if (status === "invalid" || status === "expired") {
    return (
      <PublicLayout>
        <div className="flex-1 flex items-center justify-center py-16 px-4">
          <Card className="w-full max-w-sm p-6 sm:p-8 rounded-2xl text-center space-y-4" data-testid="token-invalid-card">
            <XCircle className="h-10 w-10 mx-auto text-red-500" />
            <h1 className="text-xl font-bold tracking-tight">
              {status === "expired" ? "Link Expired" : "Invalid Link"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {status === "expired"
                ? "This password setup link has expired. Please contact your admin for a new one."
                : "This link is no longer valid. It may have already been used."}
            </p>
            <Button
              onClick={() => nav("/login")}
              className="rounded-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
              data-testid="back-to-login-btn"
            >
              Back to login
            </Button>
          </Card>
        </div>
      </PublicLayout>
    );
  }

  if (status === "done") {
    return (
      <PublicLayout>
        <div className="flex-1 flex items-center justify-center py-16 px-4">
          <Card className="w-full max-w-sm p-6 sm:p-8 rounded-2xl text-center space-y-4" data-testid="password-set-success">
            <CheckCircle2 className="h-10 w-10 mx-auto text-green-500" />
            <h1 className="text-xl font-bold tracking-tight">Password Set!</h1>
            <p className="text-sm text-muted-foreground">
              Your password has been created. Redirecting to the members portal…
            </p>
          </Card>
        </div>
      </PublicLayout>
    );
  }

  // status === "valid"
  return (
    <PublicLayout>
      <div className="flex-1 flex items-center justify-center py-16 px-4">
        <Card className="w-full max-w-sm p-6 sm:p-8 rounded-2xl space-y-6" data-testid="set-password-card">
          <div className="text-center space-y-1">
            <KeyRound className="h-8 w-8 mx-auto text-[hsl(var(--accent))]" />
            <h1 className="text-xl font-bold tracking-tight">Create Your Password</h1>
            {tokenInfo && (
              <p className="text-sm text-muted-foreground">
                Welcome{tokenInfo.first_name ? `, ${tokenInfo.first_name}` : ""}! Your Member ID is{" "}
                <span className="font-semibold text-foreground">{tokenInfo.member_id}</span>
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {tokenInfo?.member_id && (
              <div className="rounded-lg bg-[hsl(var(--secondary))] p-3 text-center">
                <p className="text-xs text-muted-foreground">Your Member ID (login)</p>
                <p className="text-lg font-bold tracking-wider" data-testid="displayed-member-id">{tokenInfo.member_id}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showPw ? "text" : "password"}
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  onClick={() => setShowPw(!showPw)}
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type={showPw ? "text" : "password"}
                placeholder="Re-enter your password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="confirm-password-input"
              />
              {confirm && password !== confirm && (
                <p className="text-xs text-red-500">Passwords don't match</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full rounded-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
              disabled={saving || password.length < 6 || password !== confirm}
              data-testid="set-password-submit-btn"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Setting password…</>
              ) : (
                <><KeyRound className="h-4 w-4 mr-2" /> Set password & enter portal</>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </PublicLayout>
  );
}
